import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  HttpOptions,
  createHttpLink,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import fetch from 'isomorphic-unfetch';
import { LOCALSTORAGE_TOKEN_KEY } from '@constants/localStorage';
import { getLegoUrl } from '../config';
import { Channel } from '@monofront/business/constants';

let apolloClient: ApolloClient<NormalizedCacheObject>;

if (typeof window === 'undefined') {
  global.fetch = fetch;
}

function create(channel: Channel, initialState, { fetchOptions }: HttpOptions) {
  const httpLink = createHttpLink({
    uri: `${getLegoUrl()}/graphql`,
    fetchOptions,
    credentials: 'include',
  });

  const authLink = setContext((_, { headers }) => {
    const token = global.localStorage?.getItem(LOCALSTORAGE_TOKEN_KEY);

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        'lf-channel': channel,
      },
    };
  });

  const removeTypenameFromVariablesLink = removeTypenameFromVariables();

  const link = from([removeTypenameFromVariablesLink, httpLink]);

  const isRunningOnBrowser = typeof window !== 'undefined';

  return new ApolloClient({
    connectToDevTools:
      process.env.NODE_ENV !== 'production' && isRunningOnBrowser,
    ssrMode: !isRunningOnBrowser,
    link: authLink.concat(link),
    cache: new InMemoryCache({
      possibleTypes: {
        Image: ['Media', 'PreviewImage', 'CartImage'],
        CustomerPaymentMethod: ['Card', 'BankAccount'],
      },
      typePolicies: {
        AddressCartV2: {
          /*Need this to calculate an unique object id for shipping and billing addresses
           Without this, graphQL cache for shipping and billing addresses in checkoutCart object have "null" as ID, which lead to conflicts between them
           They are useful for addShippingAndBillingAddress and addBillingAddress */
          keyFields: ['hash', 'id'],
        },
      },
    }).restore(initialState || {}),
  });
}

const initApollo = (
  channel: Channel,
  initialState: NormalizedCacheObject = {},
  options: HttpOptions = {},
) => {
  if (typeof window === 'undefined') {
    const fetchOptions = {};

    return create(channel, initialState, {
      ...options,
      fetchOptions,
    });
  }

  if (!apolloClient) {
    apolloClient = create(channel, initialState, options);
  }

  return apolloClient;
};

export default initApollo;
